/* global TPDirect */
import React from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';

import Joi from '@hapi/joi';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { ErrorMessage } from '@hookform/error-message';
import formErrorMessage from '@/configs/formErrorMessage';

import helper, { DELIVERY_METHOD } from '@/helper';
import Header from '@/containers/Layout/Header';
import config from '@/configs/config';
import deliveryLocationConfig from '@/configs/deliveryLocation';
import { useAuthState } from '@/plugins/Auth';
import { useProgressContext } from '@/contexts/useProgressContext';
import { useFridgeContext } from '@/contexts/useFridgeContext';

import Alert from '@/components/Alert';
import TextField from '@/components/TextField';
import { useShoppingCartContext } from '@/contexts/useShoppingCartContext';
import preOrderAPI from '@/api/preOrder';
import MagicCodeInput from './MagicCodeInput';

export const PAYMENT_METHOD = {
  LINE_PAY: 'LINE_PAY',
  TAP_PAY: 'TAP_PAY',
  TAP_PAY_3D: 'TAP_PAY_3D',
  ENTERPRISE_CARD_PAY: 'ENTERPRISE_CARD_PAY',
};

export const PAYMENT_INPUT_STYLE = {
  height: '40px',
  padding: '10px',
  borderRadius: '2px',
  backgroundColor: 'white',
};

export const DEFAULT_STYLES = {
  input: {
    color: 'black',
    'font-size': '20px',
  },
  ':focus': {},
  '.valid': { color: 'green' },
  '.invalid': { color: 'red' },
  '@media screen': { '*': { 'font-family': 'Montserrat' } },
};

const DEFAULT_FIELDS = {
  number: {
    element: '#card-number',
    placeholder: '**** **** **** ****',
  },
  expirationDate: {
    element: '#card-expiration-date',
    placeholder: 'MM / YY',
  },
  ccv: {
    element: '#card-ccv',
    placeholder: 'ccv',
  },
};

const getLinePayRedirectUrl = (href) => {
  const { origin, pathname, search } = new URL(href);
  const utmQueryString = helper.getUTMQueryStringFromLocalStorage();
  return `${origin}${pathname}/line-pay-payment${search ? `${search}&` : '?'}submitLinePay=true${utmQueryString ? `&${utmQueryString}` : ''}`;
};

const schema = Joi.object({
  city: Joi.string().required(),
  district: Joi.string().required(),
  address: Joi.string().required(),
  recepient: Joi.string().required(),
  phone: Joi.string()
    .pattern(/^09\d{2}-?\d{3}-?\d{3}$/, 'phoneNumber')
    .required()
    .messages({ 'string.pattern.name': '您輸入的手機號碼格式錯誤' }),
});

export const setupTappayFields = () => {
  TPDirect.card.setup({
    styles: DEFAULT_STYLES,
    fields: DEFAULT_FIELDS,
  });
};

const Checkout = () => {
  const history = useHistory();
  const { token } = useAuthState();
  const { fridgeDictionary } = useFridgeContext();
  const { removePreOrderFromShoppingCart } = useShoppingCartContext();

  const {
    start: startProgress,
    end: endProgress,
  } = useProgressContext();
  const [alert, setAlert] = React.useState(null);
  const [paymentMethod, setPaymentMethod] = React.useState(PAYMENT_METHOD.TAP_PAY);

  const [showMagicCodeInput, setShowMagicCodeInput] = React.useState(false);
  const [magicCodeData, setMagicCodeData] = React.useState(null);

  const [checkoutData, setCheckoutData] = React.useState([]);
  const [checkoutDataByProvider, setCheckoutDataByProvider] = React.useState({});
  const [deliveryFeeDataByProvider, setDeliveryFeeDataByProvider] = React.useState({});
  const [totalDeliveryFee, setTotalDeliveryFee] = React.useState(0);
  const [subTotal, setSubTotal] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [hasDeliveryItems, setHasDeliveryItems] = React.useState(false);
  const shoppingCartData = Object.keys(history?.location?.state || {})
    .filter((key) => /\d{13,}/.test(`${key}`))
    .reduce((obj, key) => {
      obj[key] = history?.location?.state[key];
      return obj;
    }, {});
  React.useEffect(() => {
    if (!shoppingCartData || Object.keys(shoppingCartData).length === 0) {
      history.replace('/pre-order/shopping-cart');
      return;
    }
    const newCheckoutData = [];
    const newCheckoutDataByProvider = {};
    const newDeliveryFeeDataByProvider = {};
    let newTotalDeliveryFee = 0;
    let newHasDeliveryItems = false;
    let newSubTotal = 0;
    Object.keys(shoppingCartData).forEach((preOrderId) => {
      const preOrder = shoppingCartData[preOrderId];
      const {
        fridgeId = null,
        date = null,
        timeSlot = null,
        batch = null,
        amount = 1,
        provider = '',
      } = preOrder;
      newCheckoutDataByProvider[provider] = {
        ...(newCheckoutDataByProvider[provider] || {}),
        [preOrderId]: preOrder,
      };
      newCheckoutData.push({
        preOrderId,
        fridgeId,
        date,
        timeSlot,
        batch,
        amount,
      });
      const basicSalePrice = helper.getBasePrice(preOrder);

      newSubTotal += (basicSalePrice * amount);
      if (!newHasDeliveryItems && fridgeId === DELIVERY_METHOD.DELIVERY) {
        newHasDeliveryItems = true;
      }
    });
    Object.keys(newCheckoutDataByProvider).forEach((provider) => {
      const checkoutDataForThisProvider = newCheckoutDataByProvider[provider];
      const deliveryFee = helper.calculateDeliveryFee(checkoutDataForThisProvider) || 0;
      newDeliveryFeeDataByProvider[provider] = deliveryFee;
      newTotalDeliveryFee += deliveryFee;
    });
    setCheckoutData(newCheckoutData);
    setCheckoutDataByProvider(newCheckoutDataByProvider);
    setDeliveryFeeDataByProvider(newDeliveryFeeDataByProvider);
    setTotalDeliveryFee(newTotalDeliveryFee);
    setSubTotal(newSubTotal);
    setTotal(newTotalDeliveryFee + newSubTotal);
    setHasDeliveryItems(newHasDeliveryItems);
    // eslint-disable-next-line
  }, [JSON.stringify(shoppingCartData || {}), history]);

  const dynamicSchema = hasDeliveryItems ? schema : null;
  const {
    errors,
    control,
    watch,
    handleSubmit,
    setValue,
    formState,
    trigger,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      city: '',
      district: '',
      address: '',
      recepient: '',
      phone: '',
    },
    resolver: joiResolver(dynamicSchema, {
      abortEarly: false,
      messages: formErrorMessage,
    }),
  });
  React.useEffect(() => {
    trigger();
  }, [hasDeliveryItems]);   //eslint-disable-line
  const watchCity = watch('city', '');

  const availableLocationDic = React.useMemo(() => helper.getAvailableLocation({
    defaultLocation: deliveryLocationConfig,
    availableLocationSymbolStrings: Object.keys(shoppingCartData || {}).map((preOrderId) => {
      const {
        fridgeId,
        deliveryAvailableLocation,
      } = shoppingCartData[preOrderId];
      if (fridgeId !== DELIVERY_METHOD.DELIVERY) return null;
      return deliveryAvailableLocation;
    }).filter((d) => d),
    // eslint-disable-next-line
  }), [JSON.stringify(shoppingCartData)]);

  const renderCheckoutDataByProvider = React.useCallback(() => (
    <>
      {
        Object.keys(checkoutDataByProvider).map((provider) => (
          <Box key={provider} mt={4} mb={2}>
            <Box style={{ whiteSpace: 'nowrap', maxWidth: '100%' }} p={2} bgcolor="rgb(242,242,242)">
              <span style={{ verticalAlign: 'middle' }}>{provider}</span>
            </Box>
            {
              Object.keys(checkoutDataByProvider[provider]).map((preOrderId) => {
                const preOrder = checkoutDataByProvider[provider][preOrderId];
                if (!preOrder) return null;
                const { amount } = preOrder;
                const basicSalePrice = helper.getBasePrice(preOrder);
                return (
                  <Box my={1} bgcolor="rgb(242,242,242)" key={preOrderId}>
                    <Grid container>
                      <Grid item xs={12} sm={5}>
                        <Box style={{ whiteSpace: 'nowrap', maxWidth: '100%' }} px={2}>

                          <Hidden only="xs">
                            <img
                              style={{
                                objectFit: 'cover', padding: '20px 10px 20px 0px', display: 'inline-block', verticalAlign: 'top',
                              }}
                              width="150px"
                              height="150px"
                              src={`${config.RESOURCE_URI}${preOrder?.coverImage}`}
                              alt=""
                            />
                            <Box
                              p={2}
                              height="100%"
                              style={{
                                display: 'inline-block', verticalAlign: 'top', maxWidth: 'calc(100% - 192px)', whiteSpace: 'normal',
                              }}
                            >
                              <Box fontSize="body1.fontSize" fontWeight="fontWeightBold" mb={2}>{preOrder?.title || ''}</Box>
                            </Box>
                          </Hidden>

                          <Hidden only="sm">
                            <img
                              style={{
                                objectFit: 'cover', padding: '5px', display: 'inline-block', verticalAlign: 'top',
                              }}
                              width="100px"
                              height="100px"
                              src={`${config.RESOURCE_URI}${preOrder?.coverImage}`}
                              alt=""
                            />
                            <Box
                              p={2}
                              height="100%"
                              style={{
                                display: 'inline-block', verticalAlign: 'top', width: 'calc(100% - 80px', whiteSpace: 'normal',
                              }}
                            >
                              <Box fontSize="body1.fontSize" fontWeight="fontWeightBold">{preOrder?.title || ''}</Box>
                              <Grid container>
                                <Grid item xs={6}>
                                  <Box>
                                    訂購數量:
                                    {amount}
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <Box textAlign="right" color="rgb(186, 71, 71)" fontWeight="bold">
                                    {`$${(amount || 0) * (basicSalePrice || 0)}`}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Hidden>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box p={2} height="100%" color="rgb(186, 4, 4)" fontSize="14px">
                          {
                            preOrder?.fridgeId === DELIVERY_METHOD.DELIVERY ? (
                              <>
                                <Box>
                                  {`宅配 運費${helper.getAppropriatePriceByRules(preOrder?.deliveryFeeRules, 1)}元 `}
                                  <Typography variant="caption">運費規則：同一廠商同一時段可合併運費</Typography>
                                </Box>
                                <Box>配達時段</Box>
                                <Box>{`${preOrder?.date} ${preOrder?.timeSlot}`}</Box>
                              </>
                            ) : (
                              <>
                                <Box>
                                  自取｜【
                                  {config.BrandNameTW}
                                  】
                                  {preOrder?.fridgeIds[preOrder?.fridgeId]}
                                </Box>
                                <Box color="grey">
                                  <Typography variant="caption">{fridgeDictionary[preOrder?.fridgeId]?.address}</Typography>
                                </Box>
                                <Box>自取日期</Box>
                                <Box>
                                  {
                                  `${
                                    moment(
                                      preOrder?.batches[preOrder?.batch]
                                      .preOrderArrivedNotificationTime
                                    ).format('YYYY-MM-DD HH:mm')
                                  } ~ ${
                                    moment(
                                        preOrder?.batches[preOrder?.batch]
                                        .redeemEndTime
                                    ).format('YYYY-MM-DD HH:mm')
                                  }`
                                }
                                </Box>
                              </>
                            )
                          }
                        </Box>
                      </Grid>
                      <Hidden only="xs">
                        <Grid item xs={2}>
                          <Box bgcolor="rgb(233,233,233)" py={4} px={1} height="100%" maxWidth="150px" ml="auto" style={{ position: 'relative' }}>
                            <Box style={{
                              left: '50%',
                              top: '50%',
                              position: 'absolute',
                              width: '100%',
                              textAlign: 'center',
                              transform: 'translate(-50%, -50%)',
                              fontWeight: 'bold',
                              fontSize: '14px',
                            }}
                            >
                              {amount}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={1}>
                          <Box bgcolor="rgb(233,233,233)" p={2} height="100%" style={{ position: 'relative' }}>
                            <Box style={{
                              left: '50%',
                              top: '50%',
                              position: 'absolute',
                              width: '100%',
                              textAlign: 'center',
                              transform: 'translate(-50%, -50%)',
                              fontWeight: 'bold',
                              fontSize: '14px',
                            }}
                            >
                              {`$${(amount || 0) * (basicSalePrice || 0)}`}
                            </Box>
                          </Box>
                        </Grid>
                      </Hidden>
                    </Grid>
                  </Box>
                );
              })
            }
            <Box style={{ whiteSpace: 'nowrap', maxWidth: '100%', textAlign: 'right' }} p={2} bgcolor="rgb(242,242,242)">
              運費: $
              {deliveryFeeDataByProvider[provider] || 0}
            </Box>
          </Box>
        ))
      }
    </>
  ), [checkoutDataByProvider, fridgeDictionary, deliveryFeeDataByProvider]);

  const handleLinePayPaymentMethod = React.useCallback(() => {
    startProgress();
    preOrderAPI.createLinePayRequestInfo({
      body: {
        token,
        linePayRedirectUrl: getLinePayRedirectUrl(window.location.href),
        preChargeAmount: total - (magicCodeData?.discountPrice || 0),
        checkoutData,
        magicCode: magicCodeData?.magicCode || null,
      },
    }).then((result) => {
      const {
        status,
        redirectUrl,
      } = result;

      if (status !== 'OK' || !redirectUrl) return;

      const { search, pathname, origin: redirectOrigin } = new URL(redirectUrl);

      if (window.location.origin === redirectOrigin) {
        history.replace(`${pathname}${search}`);

        endProgress();

        return;
      }

      window.location.href = redirectUrl;
    }).finally(() => {
      endProgress();
    });
  }, [token, startProgress, endProgress, total, checkoutData, magicCodeData, history]);

  const handleNoChargeCheckoutMethod = React.useCallback(() => {
    startProgress();
    preOrderAPI.checkout({
      body: {
        token,
        checkoutData,
        magicCode: magicCodeData?.magicCode || null,
      },
    }).then(({
      status: checkoutStatus,
      orderId,
      message,
    }) => {
      if (checkoutStatus === 'OK') {
        const utmQueryString = helper.getUTMQueryStringFromLocalStorage();
        history.push(`/pre-orders/checkout/receipt${utmQueryString ? `?${utmQueryString}` : ''}`, {
          checkoutData,
          orderId,
          total,
          totalDeliveryFee,
        });
        removePreOrderFromShoppingCart((checkoutData || []).map(({ preOrderId }) => preOrderId));
        return;
      }
      setAlert({
        title: '交易失敗',
        message,
      });
    }).finally(() => {
      endProgress();
    });
  }, [
    checkoutData,
    endProgress,
    history,
    magicCodeData,
    removePreOrderFromShoppingCart,
    startProgress,
    token,
    total,
    totalDeliveryFee,
  ]);

  const handleTapPayPaymentMethod = React.useCallback(() => {
    startProgress();
    TPDirect.card.getPrime(({ status = -1, card = {} }) => {
      if (status < 0) {
        setAlert({
          title: '交易失敗',
          message: '您輸入的信用卡資訊有錯誤，請重新輸入！',
        });
        endProgress();
        return;
      }

      preOrderAPI.checkout({
        body: {
          cardNumber: `${card?.bincode}******${card?.lastfour}`,
          cardToken: card?.prime,
          cardType: card?.type,
          token,
          checkoutData,
          magicCode: magicCodeData?.magicCode || null,
        },
      }).then(({
        status: checkoutStatus,
        orderId,
        message,
      }) => {
        if (checkoutStatus === 'OK') {
          const utmQueryString = helper.getUTMQueryStringFromLocalStorage();
          history.push(`/pre-orders/checkout/receipt${utmQueryString ? `?${utmQueryString}` : ''}`, {
            checkoutData,
            orderId,
            total,
            totalDeliveryFee,
          });
          removePreOrderFromShoppingCart((checkoutData || []).map(({ preOrderId }) => preOrderId));
          return;
        }
        setAlert({
          title: '交易失敗',
          message,
        });
      }).finally(() => {
        endProgress();
      });
    });
  }, [
    history,
    token,
    startProgress,
    endProgress,
    checkoutData,
    removePreOrderFromShoppingCart,
    total,
    totalDeliveryFee,
    magicCodeData,
  ]);

  const handleCheckout = React.useCallback((formData) => {
    if (hasDeliveryItems) {
      checkoutData.forEach((data) => {
        if (data.fridgeId === DELIVERY_METHOD.DELIVERY) {
          const {
            city,
            district,
            ...restOfFormData
          } = formData;
          data.delivery = {
            district: `${formData.city}/${formData.district}`,
            ...restOfFormData,
          };
        }
      });
    }
    localStorage.setItem('checkoutSummary', JSON.stringify({
      total,
      totalDeliveryFee,
      checkoutData,
      magicCode: magicCodeData?.magicCode || null,
    }));
    if ((total - (magicCodeData?.discountPrice || 0)) === 0) return handleNoChargeCheckoutMethod();
    if (paymentMethod === PAYMENT_METHOD.LINE_PAY) return handleLinePayPaymentMethod();
    return handleTapPayPaymentMethod();
  }, [
    hasDeliveryItems,
    paymentMethod,
    handleNoChargeCheckoutMethod,
    handleLinePayPaymentMethod,
    handleTapPayPaymentMethod,
    checkoutData,
    magicCodeData,
    total,
    totalDeliveryFee,
  ]);

  const checkoutForm = (
    <Box px={3} py={2} bgcolor="rgb(242, 242, 242)">
      { (total - Number(magicCodeData?.discountPrice || 0) !== 0) && (
      <>
        <Box mb={1}>
          <Box fontSize="subtitle1.fontSize" fontWeight="fontWeightBold">
            預購訂單明細（未結帳）
          </Box>
        </Box>
        <Box bgcolor="grey.500">
          <Divider variant="fullWidth" />
        </Box>
        <Box
          mt={4}
          mb={2}
          fontSize="subtitle1.fontSize"
          fontWeight="fontWeightBold"
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box color="grey.700">付款方式：</Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <RadioGroup
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value)}
              >
                <FormControlLabel value={PAYMENT_METHOD.LINE_PAY} control={<Radio />} label="以 LINE Pay 付款" />
                <FormControlLabel value={PAYMENT_METHOD.TAP_PAY} control={<Radio />} label="以 信用卡 付款" />
              </RadioGroup>
            </Grid>
          </Grid>
        </Box>

        <Box bgcolor="grey.500">
          <Divider variant="fullWidth" />
        </Box>
        <Box
          my={2}
          style={{
            display: paymentMethod === PAYMENT_METHOD.TAP_PAY ? 'block' : 'none',
            height: paymentMethod === PAYMENT_METHOD.TAP_PAY ? 'auto' : 0,
          }}
        >
          <Grid container>
            <Grid item xs={12} ref={setupTappayFields}>
              <Box
                fontSize="body2.fontSize"
                fontWeight="fontWeightBold"
                color="grey.700"
              >
                您的信用卡號
              </Box>
              <Box mt={1} mb={2}>
                <div id="card-number" style={PAYMENT_INPUT_STYLE} />
              </Box>
              <Box
                fontSize="body2.fontSize"
                fontWeight="fontWeightBold"
                color="grey.700"
              >
                卡片有效期限
              </Box>
              <Box mt={1} mb={2}>
                <div id="card-expiration-date" style={PAYMENT_INPUT_STYLE} />
              </Box>
              <Box
                fontSize="body2.fontSize"
                fontWeight="fontWeightBold"
                color="grey.700"
              >
                卡片背面末3碼
              </Box>
              <Box mt={1} mb={2}>
                <div id="card-ccv" style={PAYMENT_INPUT_STYLE} />
              </Box>
            </Grid>
          </Grid>
          <Divider variant="fullWidth" />
        </Box>
      </>
      )}
      <Box
        my={2}
        style={{ display: hasDeliveryItems ? 'block' : 'none' }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} md={3}>
            <Box mb={2}>
              <Box
                fontSize="subtitle1.fontSize"
                fontWeight="fontWeightBold"
              >
                宅配資訊：
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              fontSize="body2.fontSize"
              fontWeight="fontWeightBold"
              color="grey.700"
            >
              宅配收件地址
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="city"
              onChange={(data) => data}
              render={({
                onChange: triggerChange,
                    ...rest // eslint-disable-line
              }) => (
                <Select
                    {...rest} // eslint-disable-line
                  input={<TextField />}
                  onChange={(event) => {
                    setValue('district', '');
                    triggerChange(event.target.value);
                  }}
                >
                  {
                      Object.keys(availableLocationDic).map((location) => (
                        <MenuItem key={location} value={location}>{location}</MenuItem>
                      ))
                    }
                </Select>
              )}
            />
            <Box mb={3} color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="city" />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="district"
              as={(
                <Select
                  input={<TextField />}
                >
                  {
                      watchCity && Object.keys(availableLocationDic[watchCity])
                        .map((district) => (
                          <MenuItem key={district} value={district}>{district}</MenuItem>
                        ))
                    }
                </Select>
                )}
            />
            <Box mb={3} color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="district" />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="address"
              as={<TextField placeholder="街/巷/號/樓" />}
            />
            <Box mb={3} color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="address" />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              fontSize="body2.fontSize"
              fontWeight="fontWeightBold"
              color="grey.700"
            >
              宅配收件人姓名
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="recepient"
              as={<TextField />}
            />
            <Box mb={3} color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="recepient" />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              fontSize="body2.fontSize"
              fontWeight="fontWeightBold"
              color="grey.700"
            >
              宅配收件人手機
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="phone"
              as={<TextField />}
            />
            <Box mb={3} color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="phone" />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              fontSize="caption.fontSize"
              fontWeight="fontWeightBold"
              color="grey.500"
            >
              請確實填寫收件資訊以利確實配貨。
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  const checkoutActionsMobile = (
    <Box
      width={1}
      position="fixed"
      left={0}
      bottom={0}
      bgcolor="rgb(249, 249, 249)"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      zIndex={9}
    >
      <MagicCodeInput
        checkoutData={checkoutData}
        magicCodeData={magicCodeData}
        handleNewMagicCodeApplied={setMagicCodeData}
      />
      <Box
        borderTop="1px solid rgb(222, 222, 222)"
        mx="auto"
        fontSize="10px"
      >
        <Grid
          container
          alignItems="center"
          justify="flex-end"
          spacing={1}
        >
          <Grid item xs={magicCodeData ? 2 : 3}>
            <Box textAlign="right">
              <Box>商品總金額</Box>
              <Box>
                {`$${subTotal}`}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={magicCodeData ? 2 : 3}>
            <Box textAlign="right">
              <Box>運費總金額</Box>
              <Box>
                {`$${totalDeliveryFee}`}
              </Box>
            </Box>
          </Grid>
          {
            magicCodeData && (
            <Grid item xs={2}>
              <Box textAlign="right">
                <Box>{magicCodeData?.title || ''}</Box>
                <Box>
                  {`-${magicCodeData?.discountPrice}`}
                </Box>
              </Box>
            </Grid>
            )
          }
          <Grid item xs={2}>
            <Box textAlign="right">
              <Box>支付總金額</Box>
              <Box color="error.dark" fontWeight="bold">
                $
                {`${total - Number(magicCodeData?.discountPrice || 0)}`}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              fullWidth
              disabled={!formState.isValid}
            >
              <Box
                py={2}
                fontSize="14px"
                fontWeight="fontWeightBold"
              >
                下一步
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  const checkoutActionsDesktop = (
    <>
      <table style={{ color: 'rgb(83, 83, 83)', width: '100%', fontWeight: 'bold' }}>
        <tbody>
          <tr style={{ lineHeight: 3 }}>
            <td colSpan="2">訂單摘要</td>
          </tr>
          <tr>
            <td>預購金額總計</td>
            <td style={{ textAlign: 'right' }}>{`${subTotal}`}</td>
          </tr>
          <tr>
            <td>運費</td>
            <td style={{ textAlign: 'right' }}>{`${totalDeliveryFee}`}</td>
          </tr>
          {
            magicCodeData ? (
              <>
                <tr>
                  <td>{magicCodeData?.title || ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    {`-${magicCodeData?.discountPrice}`}
                  </td>
                </tr>
                <tr>
                  <td> </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      style={{ color: 'rgb(83, 83, 83)', textDecoration: 'underline', padding: 0 }}
                      onClick={() => setMagicCodeData(null)}
                    >
                      移除折扣碼
                    </Button>
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td>折扣碼</td>
                <td style={{ textAlign: 'right' }}>
                  <Button
                    style={{ color: 'rgb(83, 83, 83)', textDecoration: 'underline', padding: 0 }}
                    onClick={() => setShowMagicCodeInput(true)}
                  >
                    輸入折扣碼
                  </Button>
                </td>
              </tr>
            )
          }
          <tr style={{ lineHeight: 3 }}>
            <td>支付總金額</td>
            <td style={{ textAlign: 'right', color: 'rgb(168, 0, 0)' }}>
              $
              {`${total - Number(magicCodeData?.discountPrice || 0)}`}
            </td>
          </tr>
        </tbody>
      </table>
      <Button
        type="submit"
        variant="contained"
        color="secondary"
        fullWidth
        disabled={!formState.isValid}
      >
        <Box
          py={1}
          fontSize="body1.fontSize"
          fontWeight="fontWeightBold"
        >
          送出訂單
        </Box>
      </Button>
    </>
  );
  return (
    <Box pb="165px" minHeight="calc(100vh - 100px)">
      <form onSubmit={handleSubmit(handleCheckout)}>
        <Hidden only="xs">
          <Header />
          <Box fontWeight="bold" textAlign="center" my={5} position="relative">
            購物車清單
            <Box
              fontWeight="bold"
              style={{ cursor: 'pointer', position: 'absolute', top: 0 }}
              onClick={() => history.push('/pre-orders')}
            >
              ◀ 繼續購物
            </Box>
          </Box>

          <Box mt={5} height="50px" lineHeight="50px" borderBottom="1px solid black" borderTop="1px solid black">
            <Grid container>
              <Grid item xs={9}>
                訂購商品
              </Grid>
              <Grid item xs={2}>
                <Box maxWidth="150px" ml="auto" textAlign="center">
                  訂購數量
                </Box>
              </Grid>
              <Grid item xs={1}>
                <Box textAlign="center">
                  總計
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Hidden>
        {renderCheckoutDataByProvider()}
        <Hidden only="sm">
          {checkoutForm}
          {checkoutActionsMobile}
        </Hidden>
        <Hidden only="xs">
          <Grid container spacing={1}>
            <Grid item xs={8}>{checkoutForm}</Grid>
            <Grid item xs={4}>{checkoutActionsDesktop}</Grid>
          </Grid>
        </Hidden>
      </form>
      {
        showMagicCodeInput && (
          <MagicCodeInput
            handleClose={() => setShowMagicCodeInput(false)}
            checkoutData={checkoutData}
            handleNewMagicCodeApplied={setMagicCodeData}
          />
        )
      }
      {
          alert && (
            <Alert
              isOpen
              title={alert?.title || ''}
              content={alert?.message || ''}
              buttons={alert?.buttons || (
                <Button fullWidth onClick={() => setAlert(null)}>
                  <Box fontWeight="fontWeightBold" color="support.main">
                    確認
                  </Box>
                </Button>
              )}
            />
          )
        }
    </Box>
  );
};

export default Checkout;
